var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var DateAddConfig_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchema
import { DateAddType as DateAddTypeOrig, DateAddUnit as DateAddUnitOrig, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { BeforeDeserialized, JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { DateColumn } from './dateOperand/dateColumn';
import { DateLiteral } from './dateOperand/dateLiteral';
import './dateOperand/dateOperand'; // force import for JsonSchema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
export var DateAddUnit = DateAddUnitOrig;
export var DateAddType = DateAddTypeOrig;
const schema = {
    $id: 'urn:wisepipe-common-pipe:DateAddConfig',
    type: 'object',
    properties: {
        date: { $ref: 'urn:wisepipe-common-pipe:DateOperand' },
        interval: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:NumberToolVariable'
                },
                { type: 'number' }
            ]
        },
        alias: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        },
        addType: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:EnumToolVariable'
                },
                { type: 'string', enum: Object.values(DateAddType) }
            ]
        },
        unit: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:EnumToolVariable'
                },
                { type: 'string', enum: Object.values(DateAddUnit) }
            ]
        },
        column: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        }
    },
    required: ['date', 'alias', 'addType'],
    allOf: [
        {
            if: { properties: { addType: { const: DateAddType.Column } } },
            then: { required: ['column', 'unit'] }
        },
        {
            if: { properties: { addType: { const: DateAddType.Number } } },
            then: { required: ['interval', 'unit'] }
        }
    ]
};
let DateAddConfig = DateAddConfig_1 = class DateAddConfig extends ToolConfig {
    // old date add does not have addType property so they must default to Number and unit = ms
    // addType = None is for new date add that does not want to specify an interval, and
    // is there so that UI can disguish between the two
    constructor(date, alias, addType = DateAddType.Number, unit = DateAddUnit.Milliseconds, column, interval, customFormats) {
        super(ToolType.DATE_ADD, customFormats);
        this.date = date;
        this.interval = interval;
        this.alias = alias;
        this.addType = addType;
        this.unit = unit;
        this.column = column;
        JSON.validate(this, DateAddConfig_1);
    }
    setDefaults() {
        this.addType = DateAddType.Number;
        this.unit = DateAddUnit.Milliseconds;
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return DateAddConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return DateAddConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return DateAddConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return DateAddConfig_1.outs;
    }
    static get in() {
        return DateAddConfig_1.ins[0];
    }
    static get out() {
        return DateAddConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.DATE_ADD.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['date', 'add', 'month', 'year', 'day'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new DateAddConfig_1(this.date.applyParameters(parameters), baseVals.alias, baseVals.addType, baseVals.unit, baseVals.column, baseVals.interval, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            date: this.date.getBaseValues(parameters),
            alias: parameters.getStrVal(this.alias),
            addType: parameters.getEnumVal(this.addType),
            unit: parameters.getEnumVal(this.unit),
            column: parameters.getStrOrUndefVal(this.column),
            interval: parameters.getNumOrUndefVal(this.interval)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        const newColumnFormats = new Map();
        if (source) {
            const parameters = iConfigHelper.getParameters();
            const baseVals = this.getBaseValues(parameters);
            const dependents = this.getDependents(exp, this.in, Array.from(this.date.getColArgs(parameters)));
            if (this.addType === DateAddType.Column) {
                const column = exp.findSourceColumn(this.in, baseVals.column);
                dependents?.push(column);
            }
            exp.copyAll(this.in).makeNew(new Column(baseVals.alias, 'date'), dependents, true);
            if (this.date instanceof DateColumn) {
                const srcColumn = exp.findSourceColumn(this.in, baseVals.date.name);
                if (srcColumn?.customFormat) {
                    newColumnFormats.set(baseVals.alias, srcColumn.customFormat.copyWithAutoGeneratedTrue());
                }
            }
        }
        exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, ins) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, ins);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        const parameters = iConfigHelper.getParameters();
        const baseVals = this.getBaseValues(parameters);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        if (this.date instanceof DateLiteral && !baseVals.date.value) {
            return ToolValidationResult.getBadMetadataResult('Date value is missing');
        }
        if (this.date instanceof DateColumn && !baseVals.date.name) {
            return ToolValidationResult.getBadMetadataResult('Date column name is missing');
        }
        const inputs = new Map();
        this.date.addInputReq(parameters, inputs);
        if (baseVals.column !== undefined) {
            inputs.set(baseVals.column, ['number', 'decimal']);
        }
        const sourceValidationResult = validateSourceWithDetail(source, inputs, [baseVals.alias]);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
__decorate([
    BeforeDeserialized()
], DateAddConfig.prototype, "setDefaults", null);
DateAddConfig = DateAddConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.DATE_ADD)
], DateAddConfig);
export { DateAddConfig };
//# sourceMappingURL=dateAddConfig.js.map